window.HybridTreeVisualization = class HybridTreeVisualization {
    constructor(containerId, treeData, rootId, translations) {
        this.container = document.getElementById(containerId);
        if (!this.container) return;

        this.treeData = treeData;
        this.rootId = rootId;
        this.translations = translations;
        this.network = null;
        this.data = null;
        this.edgesMap = new Map();
        this.parentMap = new Map();
        this.edgeIdsByNode = new Map();
        this.highlightedEdgeIds = new Set();

        this.init();
    }

    init() {
        this.buildMaps();
        this.initSearch();
    }

    buildMaps() {
        this.treeData.edges.forEach(edge => {
            if (!this.edgesMap.has(edge.from)) this.edgesMap.set(edge.from, []);
            this.edgesMap.get(edge.from).push(edge.to);

            if (!this.parentMap.has(edge.to)) this.parentMap.set(edge.to, []);
            this.parentMap.get(edge.to).push(edge.from);
        });
    }

    initSearch() {
        const searchInput = document.getElementById('strain-search');
        const searchBtn = document.getElementById('search-btn');
        const searchResult = document.getElementById('search-result');

        if (!searchInput || !searchBtn) return;

        searchInput.placeholder = this.translations.searchPlaceholder || 'Search strain by name...';
        searchBtn.textContent = this.translations.search || 'Search';

        const performSearch = () => {
            const query = searchInput.value.trim().toLowerCase();
            if (!query) {
                searchResult.textContent = '';
                searchResult.style.display = 'none';
                return;
            }

            searchResult.style.display = 'block';

            const foundNode = this.treeData.nodes.find(node =>
                node.label && node.label.toLowerCase().includes(query)
            );

            if (foundNode) {
                searchResult.textContent = `${this.translations.found || 'Found'}: ${foundNode.label}`;
                searchResult.className = 'block mt-2 text-sm break-words text-green-600';
                this.focusOnNode(foundNode.id);
            } else {
                searchResult.textContent = this.translations.notFound || 'Strain not found';
                searchResult.className = 'block mt-2 text-sm break-words text-red-600';
            }
        };

        searchBtn.addEventListener('click', performSearch);
        searchInput.addEventListener('keypress', (e) => e.key === 'Enter' && performSearch());
    }

    focusOnNode(nodeId, scale = 1.5) {
        if (!this.network) return;
        const node = this.data.nodes.get(nodeId);
        if (!node) return;
        this.network.focus(nodeId, {
            scale,
            animation: { duration: 500, easingFunction: 'easeInOutQuad' }
        });
        this.highlightNode(nodeId);
    }

    createNetwork() {
        const layoutPrecomputed = !!this.treeData.layoutPrecomputed;
        const nodeCount = this.treeData.nodes.length;
        const isHuge = nodeCount > 10000;
        const nodeLevels = {};

        if (layoutPrecomputed) {
            this.treeData.nodes.forEach(n => { nodeLevels[n.id] = n.level ?? 0; });
        } else {
            nodeLevels[this.rootId] = 0;
            const queue = [this.rootId];
            const visited = new Set([this.rootId]);
            let qi = 0;
            while (qi < queue.length) {
                const currentNodeId = queue[qi++];
                const children = this.edgesMap.get(currentNodeId) || [];
                for (const childId of children) {
                    const parentIds = this.parentMap.get(childId) || [];
                    const newLevel = (parentIds.length > 0 ? Math.max(...parentIds.map(pid => nodeLevels[pid] ?? 0)) : 0) + 1;
                    if (!visited.has(childId)) {
                        visited.add(childId);
                        nodeLevels[childId] = newLevel;
                        queue.push(childId);
                    } else if (newLevel > (nodeLevels[childId] ?? 0)) {
                        nodeLevels[childId] = newLevel;
                    }
                }
            }
        }

        const nodeStyle = (level, isRoot) => {
            if (isHuge) {
                if (isRoot) return { size: 16, fontSize: 14, fontColor: '#ffff80', bg: '#ffff80', border: '#ffff80' };
                if (level === 1) return { size: 8, fontSize: 10, fontColor: '#96b18d', bg: '#a3b1a0', border: '#96b18d' };
                return { size: 5, fontSize: 9, fontColor: '#96b18d', bg: '#a3b1a0', border: '#96b18d' };
            }
            if (isRoot) return { size: 18, fontSize: 16, fontColor: '#ffff80', bg: '#ffff80', border: '#ffff80' };
            if (level === 1) return { size: 10, fontSize: 11, fontColor: '#96b18d', bg: '#a3b1a0', border: '#96b18d' };
            return { size: 6, fontSize: 10, fontColor: '#96b18d', bg: '#a3b1a0', border: '#96b18d' };
        };

        const nodesWithLevels = this.treeData.nodes.map(node => {
            const { level, x, y, fixed, ...rest } = node;
            const lvl = nodeLevels[node.id] ?? (node.id === this.rootId ? 0 : 1);
            const s = nodeStyle(lvl, node.id === this.rootId);
            const out = {
                ...rest,
                level: lvl,
                size: s.size,
                font: { size: s.fontSize, color: s.fontColor },
                color: { background: s.bg, border: s.border, highlight: '#ffff66' },
            };
            if (layoutPrecomputed && x !== undefined && y !== undefined) {
                out.x = x;
                out.y = y;
                out.fixed = true;
            }
            return out;
        });

        const edgeStyle = (minLevel) => {
            if (isHuge) {
                if (minLevel === 0) return { width: 0.6, color: '#5a6555' };
                if (minLevel === 1) return { width: 0.25, color: '#4a5048' };
                return { width: 0.2, color: '#4a5048' };
            }
            if (minLevel === 0) return { width: 0.8, color: '#5a6555' };
            if (minLevel === 1) return { width: 0.3, color: '#4a5048' };
            return { width: 0.25, color: '#4a5048' };
        };

        const edgesToShow = this.treeData.edges.map(edge => {
            const minLvl = Math.min(nodeLevels[edge.from] ?? 999, nodeLevels[edge.to] ?? 999);
            const e = edgeStyle(minLvl);
            return { ...edge, width: e.width, color: { color: e.color, highlight: '#ffff66' } };
        });

        this.data = {
            nodes: new vis.DataSet(nodesWithLevels),
            edges: new vis.DataSet(edgesToShow),
        };

        this.data.edges.get().forEach(edge => {
            if (!this.edgeIdsByNode.has(edge.from)) this.edgeIdsByNode.set(edge.from, []);
            if (!this.edgeIdsByNode.has(edge.to)) this.edgeIdsByNode.set(edge.to, []);
            this.edgeIdsByNode.get(edge.from).push(edge.id);
            this.edgeIdsByNode.get(edge.to).push(edge.id);
        });

        const options = {
            layout: {
                hierarchical: {
                    enabled: !layoutPrecomputed,
                    direction: 'UD',
                    sortMethod: 'hubsize',
                    levelSeparation: 50,
                    nodeSpacing: 100,
                    treeSpacing: 100,
                    blockShifting: true,
                    edgeMinimization: true,
                    parentCentralization: true,
                    shakeTowards: 'leaves',
                },
                improvedLayout: false,
            },
            physics: { enabled: false },
            interaction: {
                dragNodes: false,
                dragView: true,
                zoomView: true,
                navigationButtons: false,
                hover: false,
            },
            nodes: {
                shape: 'dot',
                font: { size: 11, face: 'Arial' },
                scaling: { min: 4, max: 18 },
                margin: 10,
            },
            edges: {
                arrows: { to: { enabled: true, scaleFactor: 1.2 } },
                smooth: false,
            },
        };

        this.network = new vis.Network(this.container, this.data, options);

        this.network.once('afterDrawing', () => {
            this.network.focus(this.rootId, {
                scale: 1.5,
                animation: { duration: 800, easingFunction: 'easeInOutQuad' }
            });
            const rootNode = this.data.nodes.get(this.rootId);
            if (rootNode) this.updateDetails(rootNode, this.edgesMap.get(this.rootId) || []);
        });

        this.setupEventHandlers();
    }

    setupEventHandlers() {
        this.network.on('click', (params) => {
            if (params.nodes.length > 0) {
                this.highlightNode(params.nodes[0]);
            } else {
                this.clearHighlight();
            }
        });

        this.network.on('doubleClick', (params) => {
            if (params.nodes.length > 0) {
                const node = this.data.nodes.get(params.nodes[0]);
                if (node?.url && node.url !== '#') {
                    window.location.href = node.url;
                }
            }
        });
    }

    highlightNode(nodeId) {
        const node = this.data.nodes.get(nodeId);
        if (!node) return;

        const newEdgeIds = new Set(this.edgeIdsByNode.get(nodeId) || []);
        const edgeUpdates = [];

        this.highlightedEdgeIds.forEach(id => {
            if (!newEdgeIds.has(id)) {
                edgeUpdates.push({ id, color: { color: '#4a5048', highlight: '#ffff66', opacity: 1.0 }, width: 0.25 });
            }
        });

        newEdgeIds.forEach(id => {
            edgeUpdates.push({ id, color: { color: '#ffff66', highlight: '#ffff66', opacity: 1.0 }, width: 0.5 });
        });

        if (edgeUpdates.length > 0) this.data.edges.update(edgeUpdates);
        this.highlightedEdgeIds = newEdgeIds;
        this.updateDetails(node, this.edgesMap.get(nodeId) || []);
    }

    clearHighlight() {
        const edgeUpdates = Array.from(this.highlightedEdgeIds).map(id => ({
            id, color: { color: '#4a5048', highlight: '#ffff66', opacity: 1.0 }, width: 0.25,
        }));
        if (edgeUpdates.length > 0) this.data.edges.update(edgeUpdates);
        this.highlightedEdgeIds.clear();
        const detailsDiv = document.getElementById('inner-details');
        if (detailsDiv) detailsDiv.innerHTML = '';
    }

    updateDetails(node, childIds = []) {
        const detailsDiv = document.getElementById('inner-details');
        if (!detailsDiv) return;

        const renderLinks = (ids) => {
            const seen = new Set();
            const links = ids.map(id => {
                const n = this.data.nodes.get(id);
                if (!n || seen.has(n.label)) return '';
                seen.add(n.label);
                return `<a href="${this.escape(n.url || '#')}" class="link">${this.escape(n.label)}</a>`;
            }).filter(Boolean);
            return links.length > 0 ? links.join(', ') : this.escape(this.translations.unknown);
        };

        const parentIds = this.parentMap.get(node.id) || [];
        const nodeBreeder = node.breeder ? ` <span class="text-gray-600">(${this.escape(node.breeder)})</span>` : '';

        detailsDiv.innerHTML = `
            <div class="card">
                <h3 class="mb-3">
                    ${this.escape(this.translations.centeredStrain || 'Centered Strain')}: <a href="${this.escape(node.url || '#')}" class="link">${this.escape(node.label)}</a>${nodeBreeder}
                </h3>
                <div class="space-y-2">
                    <p><strong>${this.escape(this.translations.parentStrain || 'Parent Strain')}:</strong> ${renderLinks(parentIds)}</p>
                    <p><strong>${this.escape(this.translations.parents || 'Hybrids')}:</strong> ${renderLinks(childIds)}</p>
                </div>
            </div>
        `;
    }

    escape(text) {
        if (text == null) return '';
        return String(text).replace(/[&<>"']/g, m => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[m]));
    }
}
